import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Row, Col, Table, Badge, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../api/Axios';

const PendingInspections = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchPendingInspections();
  }, []);

  const fetchPendingInspections = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = { Authorization: `Token ${token}` };

      // Fetch all properties - only show inspection_requested
      const propertiesResponse = await axiosInstance.get('/api/properties/all-created/', { headers });
      const allProperties = Array.isArray(propertiesResponse.data.properties) 
        ? propertiesResponse.data.properties 
        : (Array.isArray(propertiesResponse.data) ? propertiesResponse.data : []);
      const pendingProperties = allProperties.filter(property => 
        property.inspection_status === 'inspection_requested'
      );

      // Fetch all rooms - only show inspection_requested
      const roomsResponse = await axiosInstance.get('/api/rooms/all/', { headers });
      const allRooms = Array.isArray(roomsResponse.data) ? roomsResponse.data : [];
      const pendingRooms = allRooms.filter(room => 
        room.inspection_status === 'inspection_requested'
      );

      setProperties(pendingProperties);
      setRooms(pendingRooms);
    } catch (err) {
      console.error('Error fetching pending inspections:', err);
      setError('Failed to load pending inspections. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePassProperty = async (propertyId) => {
    try {
      const headers = { Authorization: `Token ${token}` };
      await axiosInstance.patch(
        `/api/properties/${propertyId}/`,
        { inspection_status: 'inspection_passed' },
        { headers }
      );

      setSuccessMessage('Property inspection marked as passed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Refresh the list
      fetchPendingInspections();
    } catch (err) {
      console.error('Error updating property inspection status:', err);
      setError('Failed to update property inspection status. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handlePassRoom = async (roomId) => {
    try {
      const headers = { Authorization: `Token ${token}` };
      await axiosInstance.patch(
        `/api/rooms/${roomId}/`,
        { inspection_status: 'inspection_passed' },
        { headers }
      );

      setSuccessMessage('Room inspection marked as passed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Refresh the list
      fetchPendingInspections();
    } catch (err) {
      console.error('Error updating room inspection status:', err);
      setError('Failed to update room inspection status. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'inspection_pending':
        return 'secondary';
      case 'inspection_requested':
        return 'warning';
      case 'inspection_booked':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const formatStatusText = (status) => {
    return status
      .replace('inspection_', '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">Pending Inspections</h3>
            <Button
              color="secondary"
              size="sm"
              onClick={() => navigate('/inspector-dashboard')}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Dashboard
            </Button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}
        </Col>
      </Row>

      {/* Pending Properties Section */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h5" className="mb-4">
                <i className="bi bi-building me-2"></i>
                Pending Property Inspections ({properties.length})
              </CardTitle>

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : properties.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-check-circle-fill" style={{ fontSize: '3rem' }}></i>
                  <p className="mt-3 mb-0">No pending property inspections at this time.</p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Property ID</th>
                      <th>Trading Name</th>
                      <th>Address</th>
                      <th>Provider</th>
                      <th>Status</th>
                      <th>Inspection Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {properties.map((property) => (
                      <tr key={property.id}>
                        <td>#{property.id}</td>
                        <td><strong>{property.trading_name || 'N/A'}</strong></td>
                        <td>
                          {property.addresslines && (
                            <div className="small">
                              {property.addresslines}<br />
                              {property.city}, {property.province}
                            </div>
                          )}
                        </td>
                        <td>{property.user_email || 'N/A'}</td>
                        <td>
                          <Badge color={getStatusBadgeColor(property.inspection_status)}>
                            {formatStatusText(property.inspection_status)}
                          </Badge>
                        </td>
                        <td>
                          {property.inspection_date 
                            ? new Date(property.inspection_date).toLocaleDateString()
                            : 'Not scheduled'
                          }
                        </td>
                        <td>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handlePassProperty(property.id)}
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            Pass
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Pending Rooms Section */}
      <Row className="mt-4">
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h5" className="mb-4">
                <i className="bi bi-door-closed me-2"></i>
                Pending Room Inspections ({rooms.length})
              </CardTitle>

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : rooms.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-check-circle-fill" style={{ fontSize: '3rem' }}></i>
                  <p className="mt-3 mb-0">No pending room inspections at this time.</p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Room ID</th>
                      <th>Room Number</th>
                      <th>Floor</th>
                      <th>Block</th>
                      <th>Property</th>
                      <th>Beds</th>
                      <th>Status</th>
                      <th>Inspection Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map((room) => (
                      <tr key={room.id}>
                        <td>#{room.id}</td>
                        <td><strong>{room.room_number}</strong></td>
                        <td>{room.floor_name || 'N/A'}</td>
                        <td>{room.block_name || 'N/A'}</td>
                        <td className="small">{room.property_name || 'N/A'}</td>
                        <td>{room.num_beds || 'N/A'}</td>
                        <td>
                          <Badge color={getStatusBadgeColor(room.inspection_status)}>
                            {formatStatusText(room.inspection_status)}
                          </Badge>
                        </td>
                        <td>
                          {room.inspection_date 
                            ? new Date(room.inspection_date).toLocaleDateString()
                            : 'Not scheduled'
                          }
                        </td>
                        <td>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handlePassRoom(room.id)}
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            Pass
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PendingInspections;
